import { SignupStageHandlerComponent } from "./SignupStageHandlerComponent.type";
import { Formik } from "formik";
import { renderFieldError } from "../../utils/form";
import { useCallback, useEffect, useState } from "react";
import { RegistrationFormContextShape } from "../../contexts/form";
import FormLayout from "../../layouts/form";
import { services } from "../../services";
import { uploadFile } from "../../utils/upload";
import { toFormikValidationSchema } from "zod-formik-adapter";
import { z } from "zod";
import { CreateLearnerPayload } from "@iibf/shared-resources/dto/create-learner";
import { getFileAsImage } from "../../utils/file";
import { resolvers } from "@iibf/shared-resources/utils/pincode";
import { BatchList } from "./PersonaDetails/BatchList";
import BaseLayout from "../../layouts/base";
import { error } from "console";
import DataTable from "react-data-table-component";
import { useSearchParams } from "react-router-dom";
import { courseDraMap, CourseType } from "../../config";
import { getCourseIds } from "../../utils/getCourseIds";
import { draCourseIds } from "../../config";
import usePromise from "../../utils/hooks/usePromise";
import { useLocation } from "react-router-dom";
import { PAGE_PATHS } from "../page-paths";
import moment from "moment";
import {
  examStatesAndCities,
  examLanguages,
} from "../../utils/examCitiesDetails";

interface IBatch {
  id: number;
  batch_name: string;
  from_date: string;
  to_date: string;
  slot_start_time: string;
  slot_end_time: string;
  max_learners: number;
  userEnrollmentBatchArray_aggregate: {
    aggregate: {
      count: number;
    };
  };
  language: {
    display_name: string;
  };
  course: {
    id: number;
    full_name: string;
  };
  registration_end_date: string;
}

// Temporary maintenance toggle for DRA batches (DRA-GRADUATE & DRA-UNDERGRADUATE)
const IS_DRA_UNDER_MAINTENANCE = true;

export const PersonalDetailsPage: SignupStageHandlerComponent = ({
  stepNumber,
  stepTitle,
  formState,
  setFieldValue,
  clearForm,
}) => {
  const [showBatchSelection, setShowBatchSelection] = useState(true);
  const [batchData, setBatchData] = useState<IBatch[]>([]);
  const [selectedBatch, setSelectedBatch] = useState<IBatch>();
  const [showPopup, setShowPopup] = useState(false);
  const [activeSampleImage, setActiveSampleImage] = useState<string | null>(
    null,
  );
  const [zoomLevel, setZoomLevel] = useState(1);
  const [popupAcknowledged, setPopupAcknowledged] = useState(false);
  const [searchParams] = useSearchParams();
  const [partners, setPartners] = useState([]);
  const [error, setError] = useState("");
  const [selectedExamState, setSelectedExamState] = useState("");
  const [examCityOptions, setExamCityOptions] = useState<string[]>([]);
  const course = searchParams.get("course") as CourseType | null;
  const course_ids = course ? courseDraMap[course] : undefined;

  const location = useLocation();
  const isSelfOnboarding = location.pathname.includes(PAGE_PATHS.SIGNUP_SELF);
  async function getAvailableBatches() {
    let url = "/get-available-batches";
    let params: any = {};
    const courseIdsSpecificHostname = getCourseIds();

    if (course_ids) params.course_ids = course_ids;
    else if (courseIdsSpecificHostname.length > 0)
      params.course_ids = courseIdsSpecificHostname;

    try {
      const response = await services.requestSvc.get(url, { params });
      setBatchData(response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching batches:", error);
      return [];
    }
  }

  useEffect(() => {
    if (showBatchSelection) {
      getAvailableBatches();
    }
  }, [showBatchSelection]);

  // const handleBatchSubmit = () => {
  //   setFieldValue("batchId", selectedBatch);
  // };
  const closeSampleModal = () => {
    setActiveSampleImage(null);
    setZoomLevel(1);
  };
  const handleSubmit = useCallback(
    async (values: Partial<PersonalDetailsFormrResponseShape>) => {
      setFieldValue("isPersonalDetailsFinalised", true);
      setFieldValue("name", values.name);
      setFieldValue("salutation", values.salutation);
      setFieldValue("email", values.email);
      setFieldValue("mobileNumber", values.mobileNumber);
      setFieldValue("gender", values.gender);
      setFieldValue("dateOfBirth", values.dateOfBirth);
      setFieldValue("category", values.category);
      setFieldValue("fatherName", values.fatherName);
      setFieldValue("isPhysicallyChallenged", values.isPhysicallyChallenged);
      setFieldValue("highestQualification", values.highestQualification);
      setFieldValue("addressLine1", values.addressLine1);
      setFieldValue("pincode", values.pincode);
      setFieldValue("identityProofType", values.identityProofType);
      setFieldValue("identityProofNumber", values.identityProofNumber);
      setFieldValue("batchId", selectedBatch?.id);
      setFieldValue("courseId", selectedBatch?.course.id);
      setFieldValue("partnerId", values.partnerId || "");
      setFieldValue("examCenterState", values.examCenterState || "");
      setFieldValue("examCenterCity", values.examCenterCity || "");
      setFieldValue("examLanguage", values.examLanguage || "");
      const latestBatchData = await getAvailableBatches();

      const currentBatch = latestBatchData.find(
        (b: any) => b.id === selectedBatch?.id,
      );
      if (!currentBatch) {
        alert(
          "Selected batch not found or filled. Please choose a new batch again.",
        );
        setShowBatchSelection(true);
        return;
      } else {
        try {
          await uploadFileAndSetIdAsValue(
            values.identityProofFile!,
            setFieldValue,
            "identityProofFileId",
          );
          await uploadFileAndSetIdAsValue(
            values.highestQualificationCertificateFile!,
            setFieldValue,
            "highestQualificationCertificateFileId",
          );
          await uploadFileAndSetIdAsValue(
            values.candidatePhotoFile!,
            setFieldValue,
            "candidatePhotoFileId",
          );
          await uploadFileAndSetIdAsValue(
            values.candidateSignatureFile!,
            setFieldValue,
            "candidateSignatureFileId",
          );

          // Validate payload

          await services.requestSvc.post(
            "/validate-learner-payload",
            formState,
          );

          setFieldValue("personalDetailsErr", undefined);
          setFieldValue("isPersonalDetailsVerified", true);
        } catch (err: any) {
          setFieldValue("personalDetailsErr", err.message);
          setFieldValue("isPersonalDetailsVerified", false);
        }
      }
    },
    [setFieldValue, selectedBatch],
  );
  const {
    result: fetchPartnersRes,
    error: fetchPartnersErr,
    loading: fetchingPartners,
  } = usePromise(
    {
      fn: async () => {
        return await services.requestSvc.get("/partners");
      },
      runOnMount: { args: [] },
      runOnRerender: { enabled: true, args: [] },
    },
    [],
  );

  useEffect(() => {
    if (fetchPartnersRes) {
      console.log("fetchPartnersRes.data", fetchPartnersRes);
      setPartners(fetchPartnersRes);
    }
    if (fetchPartnersErr) {
      setError("Error fetching partners.");
    }
  }, [fetchPartnersRes, fetchPartnersErr]);
  const stateCityMap: Record<string, string[]> = examStatesAndCities;
  useEffect(() => {
    if (selectedExamState) {
      setExamCityOptions(stateCityMap[selectedExamState] || []);
    } else {
      setExamCityOptions([]);
    }
  }, [selectedExamState]);
  const courseIdsSpecificHostname = getCourseIds();
  const isDraCourse =
    course === "dra-graduate" ||
    course === "dra-undergraduate" ||
    Boolean(
      courseIdsSpecificHostname?.some((id) => draCourseIds.includes(id)),
    ) ||
    batchData?.some(
      (b) =>
        b.batch_name?.toUpperCase().includes("DRA-GRADUATE") ||
        b.batch_name?.toUpperCase().includes("DRA-UNDERGRADUATE"),
    );
  const isDRA = isDraCourse;
  const isDraUnderMaintenance = IS_DRA_UNDER_MAINTENANCE && isDraCourse;
  console.log("batchData!@", batchData);
  const personalDetailForm = () => {
    return (
      <FormLayout
        stepNumber={stepNumber}
        stepTitle={stepTitle || ""}
        clearForm={clearForm}
      >
        <div className="italic mb-4">
          {selectedBatch && (
            <div className="mb-2 text-lg font-semibold text-gray-700">
              Selected Batch:{" "}
              <span className="text-blue-600">{selectedBatch.batch_name}</span>
            </div>
          )}
          <span className="text-lg font-semibold text-orange-500">NOTICE</span>
          <p>
            Please be ready with the following documents before the registration
            process.
            <ul className="list-disc ml-4">
              <li>
                Highest Qualification Certificate document: png/jpg format, less
                than 3MB
              </li>
              <li>Identity Proof Document: png/jpg format, less than 3MB</li>
              <li>
                Candidate Passport Size Photo: png/jpg format, less than 3MB
              </li>
              <li>Candidate Signature: png/jpg format, less than 3MB</li>
              <li>
                Submit the highest qualification certificate document according
                to your selected batch category.
              </li>
            </ul>
          </p>
        </div>
        <Formik<Partial<PersonalDetailsFormrResponseShape>>
          initialValues={{
            name: formState.name,
            salutation: formState.salutation,
            mobileNumber: formState.mobileNumber,
            email: formState.email,
            gender: formState.gender,
            dateOfBirth: formState.dateOfBirth,
            category: formState.category,
            fatherName: formState.fatherName,
            isPhysicallyChallenged: formState.isPhysicallyChallenged,
            highestQualification: formState.highestQualification,
            identityProofType: formState.identityProofType,
            identityProofNumber: formState.identityProofNumber,
            addressLine1: formState.addressLine1,
            pincode: formState.pincode,
            batchId: formState.batchId,
            courseId: formState.courseId,
            partnerId: formState.partnerId,
            examCenterState: formState.examCenterState,
            examCenterCity: formState.examCenterCity,
            examLanguage: formState.examLanguage,
          }}
          onSubmit={handleSubmit}
          validationSchema={toFormikValidationSchema(
            PersonalDetailsFormResponseSchema,
          )}
        >
          {({
            values,
            errors,
            handleChange,
            handleBlur,
            handleSubmit,
            setFieldValue,
            isSubmitting,
          }) => {
            return (
              <form className="mt-3" onSubmit={handleSubmit}>
                {formState.personalDetailsErr ? (
                  <div className="text-red-500 mb-4 font-semibold">
                    {formState.personalDetailsErr}
                  </div>
                ) : (
                  ""
                )}
                <div className="flex flex-col gap-y-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-y-2 gap-x-2 items-start">
                    <div className="col-span-2 xl:col-span-3 font-semibold mb-4 text-lg">
                      Personal Details
                    </div>
                    <div className="flex flex-col w-full gap-y-1">
                      <span className="font-semibold">Salutation</span>
                      <select
                        className="input-primary"
                        name="salutation"
                        onChange={handleChange}
                        defaultValue={formState.salutation}
                      >
                        <option value="">Choose salutation</option>
                        <option value="mr">Mr</option>
                        <option value="mrs">Mrs</option>
                        <option value="miss">Miss</option>
                      </select>
                      {renderFieldError(errors, "salutation")}
                    </div>
                    <div className="flex flex-col w-full gap-y-1">
                      <span className="font-semibold">Name</span>
                      <input
                        type="text"
                        name="name"
                        onChange={(e) =>
                          setFieldValue("name", e.target.value.toUpperCase())
                        }
                        onBlur={(e) =>
                          setFieldValue("name", e.target.value.toUpperCase())
                        }
                        value={values.name}
                        defaultValue={values.name}
                        placeholder="Name"
                        className="input-primary"
                      />
                      <span className="text-blue text-xs font-semibold">
                        Full name MUST match uploaded identity proof.
                      </span>
                      {renderFieldError(errors, "name")}
                    </div>
                    <div className="flex flex-col w-full gap-y-1">
                      <span className="font-semibold">Mobile Number</span>
                      <input
                        type="text"
                        name="mobileNumber"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        defaultValue={values.mobileNumber}
                        placeholder="Mobile Number"
                        className="input-primary"
                      />
                      <span className="text-blue text-xs font-semibold">
                        Must be active and available on WhatsApp. All future updates and communications from IIBF/ CSC will be sent to this number.
                      </span>
                      {renderFieldError(errors, "mobileNumber")}
                    </div>
                    <div className="flex flex-col w-full gap-y-1">
                      <span className="font-semibold">Gender</span>
                      <select
                        className="input-primary"
                        name="gender"
                        onChange={handleChange}
                        defaultValue={values.gender}
                      >
                        <option>Choose gender</option>
                        <option value="male">Male</option>
                        <option value="female">Female</option>
                        <option value="other">Other</option>
                      </select>
                      {renderFieldError(errors, "gender")}
                    </div>
                    <div className="flex flex-col w-full gap-y-1">
                      <span className="font-semibold">Email</span>
                      <input
                        type="text"
                        name="email"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        defaultValue={values.email}
                        placeholder="Email"
                        className="input-primary"
                      />
                      <span className="text-blue text-xs font-semibold">
                        Must be correct and active as IIBF Non-Member Portal details will be sent to this email upon batch enrolment.
                      </span>
                      {renderFieldError(errors, "email")}
                    </div>

                    <div className="flex flex-col w-full gap-y-1">
                      <span className="font-semibold">
                        Date of Birth(Minimum Age: 18 Years)
                      </span>
                      <input
                        type="date"
                        name="dateOfBirth"
                        onChange={(e) => {
                          try {
                            return setFieldValue(
                              "dateOfBirth",
                              new Date(e.target.value).toISOString(),
                            );
                          } catch (err) { }
                        }}
                        onBlur={(e) => {
                          try {
                            return setFieldValue(
                              "dateOfBirth",
                              new Date(e.target.value).toISOString(),
                            );
                          } catch (err) { }
                        }}
                        defaultValue={values.dateOfBirth?.split("T")[0]}
                        placeholder="Date of Birth"
                        className="input-primary"
                      />
                      {renderFieldError(errors, "dateOfBirth")}
                    </div>
                    <div className="flex flex-col w-full gap-y-1">
                      <span className="font-semibold">Category</span>
                      <select
                        name="category"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        defaultValue={values.category}
                        className="input-primary"
                      >
                        <option value="">Choose a category</option>
                        <option value="general">General</option>
                        <option value="sc">SC</option>
                        <option value="st">ST</option>
                        <option value="obc">OBC</option>
                      </select>
                      {renderFieldError(errors, "category")}
                    </div>
                    <div className="flex flex-col w-full gap-y-1">
                      <span className="font-semibold">Father's Name</span>
                      <input
                        type="text"
                        name="fatherName"
                        onChange={(e) =>
                          setFieldValue(
                            "fatherName",
                            e.target.value.toUpperCase(),
                          )
                        }
                        onBlur={(e) =>
                          setFieldValue(
                            "fatherName",
                            e.target.value.toUpperCase(),
                          )
                        }
                        value={values.fatherName}
                        defaultValue={values.fatherName}
                        placeholder="Father's Name"
                        className="input-primary"
                      />
                      {renderFieldError(errors, "fatherName")}
                    </div>
                    <div className="flex flex-col w-full gap-y-1">
                      <span className="font-semibold">
                        Is Physically Challenged?
                      </span>
                      <select
                        name="isPhysicallyChallenged"
                        onChange={(e) => {
                          return setFieldValue(
                            "isPhysicallyChallenged",
                            e.target.value === "true" ? true : false,
                          );
                        }}
                        defaultValue={
                          typeof values.isPhysicallyChallenged === "boolean"
                            ? values.isPhysicallyChallenged
                              ? "true"
                              : "false"
                            : ""
                        }
                        className="input-primary"
                      >
                        <option value="">Choose an option</option>
                        <option value="true">Yes</option>
                        <option value="false">No</option>
                      </select>
                      {renderFieldError(errors, "isPhysicallyChallenged")}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-y-2 gap-x-2 items-start">
                    <div className="col-span-2 xl:col-span-3 font-semibold mb-4 text-lg">
                      Address Details
                    </div>
                    <div className="flex flex-col w-full gap-y-1 col-span-2">
                      <span className="font-semibold">Complete Address</span>
                      <input
                        type="text"
                        name="addressLine1"
                        onChange={(e) =>
                          setFieldValue(
                            "addressLine1",
                            e.target.value.toUpperCase(),
                          )
                        }
                        onBlur={(e) =>
                          setFieldValue(
                            "addressLine1",
                            e.target.value.toUpperCase(),
                          )
                        }
                        value={values.addressLine1}
                        defaultValue={values.addressLine1}
                        placeholder="Complete Address"
                        className="input-primary"
                      />
                      {renderFieldError(errors, "addressLine1")}
                    </div>
                    <div className="flex flex-col w-full gap-y-1">
                      <span className="font-semibold">Pincode</span>
                      <input
                        type="text"
                        name="pincode"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        defaultValue={values.pincode}
                        placeholder="Pincode"
                        className="input-primary"
                      />
                      {renderFieldError(errors, "pincode")}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-2 items-start">
                    <div className="col-span-2 xl:col-span-3 font-semibold mb-4 text-lg">
                      Education Details
                    </div>
                    <div className="flex flex-col w-full gap-y-1">
                      <span className="font-semibold">
                        Highest Qualification
                      </span>
                      <select
                        name="highestQualification"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        defaultValue={values.highestQualification}
                        className="input-primary"
                      >
                        <option value="">Choose a qualification</option>
                        {[
                          { value: "q_10", label: "10th Standard" },
                          { value: "q_12", label: "12th Standard" },
                          { value: "q_graduate", label: "Graduate" },
                          { value: "q_post_graduate", label: "Post Graduate" },
                        ].map((o) => (
                          <option value={o.value}>{o.label}</option>
                        ))}
                      </select>
                      {renderFieldError(errors, "highestQualification")}
                    </div>

                    <div className="flex flex-col w-full gap-y-1">
                      <span className="font-semibold">
                        Highest Qualification Certificate
                      </span>

                      <input
                        type="file"
                        name="highestQualificationCertificateFile"
                        onChange={(e) => {
                          setFieldValue(
                            "highestQualificationCertificateFile",
                            e.target.files?.[0],
                          );
                        }}
                        placeholder="Choose Highest Qualification File"
                        className="input-primary"
                        accept=".png, .jpg, .jpeg"
                      />
                      <span className="text-blue text-xs font-semibold">
                        png/jpg format, less than 3MB
                      </span>

                      {renderFieldError(
                        errors,
                        "highestQualificationCertificateFile",
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-2 items-start">
                    <div className="col-span-2 xl:col-span-3 font-semibold mb-4 text-lg">
                      Identity Documents
                    </div>
                    <div className="col-span-2 xl:col-span-3 bg-blue-50 border-2 border-black rounded-lg p-4 mb-4 text-sm text-gray-700">
                      <p className="font-bold text-blue-900 mb-1">
                        Sample Image Quality (For Submitted Documents Purpose)
                      </p>
                      <p className="leading-relaxed mb-3">
                        Review these sample images to ensure your uploaded
                        documents are clear, fully visible, and meet submission
                        standards. Your documents must be completely unblurred
                        with no parts hidden or cut off. When uploading your
                        photograph, ensure it is a real, current photo of your
                        appearance; do not take a picture of an existing
                        passport photo.
                      </p>
                      <div className="flex flex-wrap gap-x-4 gap-y-2 font-semibold">
                        <button
                          type="button"
                          onClick={() => setActiveSampleImage("photo")}
                          className="text-red-600 hover:underline text-xs flex items-center gap-1 focus:outline-none"
                        >
                          👁️ Photo Sample
                        </button>
                        <button
                          type="button"
                          onClick={() => setActiveSampleImage("signature")}
                          className="text-red-600 hover:underline text-xs flex items-center gap-1 focus:outline-none"
                        >
                          👁️ Signature Sample
                        </button>
                        <button
                          type="button"
                          onClick={() => setActiveSampleImage("id_card")}
                          className="text-red-600 hover:underline text-xs flex items-center gap-1 focus:outline-none"
                        >
                          👁️ ID Card Sample
                        </button>
                        <button
                          type="button"
                          onClick={() => setActiveSampleImage("degree")}
                          className="text-red-600 hover:underline text-xs flex items-center gap-1 focus:outline-none"
                        >
                          👁️ Degree Sample
                        </button>
                      </div>
                    </div>
                    <div className="flex flex-col w-full gap-y-1">
                      <span className="font-semibold">Identity Proof Type</span>
                      <select
                        name="identityProofType"
                        onChange={handleChange}
                        onBlur={handleBlur}
                        defaultValue={values.identityProofType}
                        className="input-primary"
                      >
                        <option value="">Choose a proof type</option>
                        {[
                          {
                            value: "driving_license",
                            label: "Driving License",
                          },
                          { value: "employers_card", label: "Employer's Card" },
                          { value: "pan", label: "PAN Card" },
                          { value: "passport", label: "Passport" },
                        ].map((o) => (
                          <option value={o.value}>{o.label}</option>
                        ))}
                      </select>
                      {renderFieldError(errors, "identityProofType")}
                    </div>
                    <div className="flex flex-col w-full gap-y-1">
                      <span className="font-semibold">
                        Identity Proof Number
                      </span>
                      <input
                        type="text"
                        name="identityProofNumber"
                        onChange={(e) =>
                          setFieldValue(
                            "identityProofNumber",
                            e.target.value.toUpperCase(),
                          )
                        }
                        onBlur={(e) =>
                          setFieldValue(
                            "identityProofNumber",
                            e.target.value.toUpperCase(),
                          )
                        }
                        value={values.identityProofNumber}
                        defaultValue={values.identityProofNumber}
                        placeholder="Identity Proof Number"
                        className="input-primary"
                      />
                      {renderFieldError(errors, "identityProofNumber")}
                    </div>
                    <div className="flex flex-col w-full gap-y-1">
                      <span className="font-semibold">Identity Proof File</span>
                      <input
                        type="file"
                        name="identityProofFile"
                        onChange={(e) => {
                          setFieldValue(
                            "identityProofFile",
                            e.target.files?.[0],
                          );
                        }}
                        placeholder="Choose Identity Proof File"
                        className="input-primary"
                        accept=".png, .jpg, .jpeg"
                      />
                      <span className="text-blue text-xs font-semibold">
                        png/jpg format, less than 3MB
                      </span>

                      {renderFieldError(errors, "identityProofFile")}
                    </div>
                    <div className="flex flex-col w-full gap-y-1">
                      <span className="font-semibold">Passport Size Photo</span>
                      <input
                        type="file"
                        name="candidatePhotoFile"
                        onChange={(e) => {
                          setFieldValue(
                            "candidatePhotoFile",
                            e.target.files?.[0],
                          );
                        }}
                        placeholder="candidatePhotoFile"
                        className="input-primary"
                        accept=".png, .jpg, .jpeg"
                      />
                      <span className="text-blue text-xs font-semibold">
                        png/jpg format, less than 3MB
                      </span>
                      <span className="text-blue text-xs font-semibold">
                        Please upload your current photo only.
                      </span>

                      {renderFieldError(errors, "candidatePhotoFile")}
                    </div>
                    <div className="flex flex-col w-full gap-y-1">
                      <span className="font-semibold">Signature</span>
                      <input
                        type="file"
                        name="candidateSignatureFile"
                        onChange={(e) => {
                          setFieldValue(
                            "candidateSignatureFile",
                            e.target.files?.[0],
                          );
                        }}
                        placeholder="Choose candidate signature file"
                        className="input-primary"
                        accept=".png, .jpg, .jpeg"
                      />
                      <span className="text-blue text-xs font-semibold">
                        png/jpg format, less than 3MB
                      </span>

                      {renderFieldError(errors, "candidateSignatureFile")}
                    </div>

                    {/* <div className="flex flex-col w-full gap-y-1">
                      <span className="font-semibold">
                        Choose Partner(If any)
                      </span>
                      <select
                        name="partnerId"
                        onChange={(e) =>
                          setFieldValue("partnerId", e.target.value)
                        }
                        defaultValue={values.partnerId}
                        className="input-primary"
                      >
                        <option value="">Choose Partner</option>
                        {error ? (
                          <option>{error}</option>
                        ) : (
                          partners?.map((partner: any) => (
                            <option key={partner.id} value={partner.id}>
                              {partner.name}
                            </option>
                          ))
                        )}
                      </select>
                      {renderFieldError(errors, "partnerId")}
                    </div> */}
                  </div>
                  {isDRA && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-y-2 gap-x-2 items-start">
                      <div className="col-span-2 xl:col-span-3 font-semibold mb-4 text-lg">
                        Exam Center Related Details
                      </div>
                      <div className="flex flex-col w-full gap-y-1">
                        <span className="font-semibold">Exam Center State</span>
                        <select
                          name="examCenterState"
                          onChange={(e) => {
                            const state = e.target.value;
                            setSelectedExamState(state);
                            setFieldValue("examCenterState", state);
                          }}
                          value={values.examCenterState}
                          defaultValue={values.examCenterState}
                          className="input-primary"
                          required
                        >
                          <option value="">Choose a state</option>
                          {Object.keys(stateCityMap).map((state) => (
                            <option key={state} value={state}>
                              {state}
                            </option>
                          ))}
                        </select>
                        {renderFieldError(errors, "examCenterState")}
                      </div>

                      <div className="flex flex-col w-full gap-y-1">
                        <span className="font-semibold">Exam Center City</span>
                        <select
                          name="examCenterCity"
                          onChange={(e) => {
                            const city = e.target.value;
                            setFieldValue("examCenterCity", city);
                          }}
                          value={values.examCenterCity}
                          defaultValue={values.examCenterCity}
                          className="input-primary"
                          disabled={!examCityOptions.length}
                          required
                        >
                          <option value="">Choose a city</option>
                          {examCityOptions.map((city) => (
                            <option key={city} value={city}>
                              {city}
                            </option>
                          ))}
                        </select>
                        {renderFieldError(errors, "examCenterCity")}
                      </div>

                      <div className="flex flex-col w-full gap-y-1">
                        <span className="font-semibold">Exam Language</span>
                        <select
                          name="examLanguage"
                          onChange={(e) => {
                            setFieldValue("examLanguage", e.target.value);
                          }}
                          value={values.examLanguage}
                          defaultValue={values.examLanguage}
                          className="input-primary"
                          required
                        >
                          <option value="">Choose exam language</option>
                          {examLanguages.map((lang) => (
                            <option key={lang} value={lang}>
                              {lang}
                            </option>
                          ))}
                        </select>
                        {renderFieldError(errors, "examLanguage")}
                      </div>
                    </div>
                  )}
                </div>
                <div className="flex flex-col mt-4">
                  <span>
                    <input
                      type="checkbox"
                      name="acceptedTC"
                      onChange={(e) =>
                        setFieldValue("acceptedTC", e.target.checked)
                      }
                      onBlur={(e) =>
                        setFieldValue("acceptedTC", e.target.checked)
                      }
                      checked={values.acceptedTC}
                      className="mr-2"
                    />
                    I confirm that the candidate is being enrolled for the
                    above-mentioned examination. I have ensured that all Rules,
                    Regulations, and instructions governing the examination of
                    the institute have been read and understood. The candidate
                    agrees to abide by all the said Rules, Regulations, and
                    instructions of the institute. It is also confirmed that the
                    candidate has not been debarred or disqualified from
                    appearing in the institute's examinations at the time of
                    this application. Additionally, it is agreed that any legal
                    proceedings initiated by the candidate against the institute
                    shall only be instituted in the Courts at New Delhi, and not
                    in any other Court.
                  </span>
                  {renderFieldError(errors, "acceptedTC")}
                </div>
                <div className="flex flex-col mt-4">
                  <span>
                    <input
                      type="checkbox"
                      name="agreedTC"
                      onChange={(e) =>
                        setFieldValue("agreedTC", e.target.checked)
                      }
                      onBlur={(e) =>
                        setFieldValue("agreedTC", e.target.checked)
                      }
                      checked={values.agreedTC}
                      className="mr-2"
                    />
                    I confirm that all the details which have been shared and
                    all the documents which have been uploaded are correct and
                    won’t be changed in future. I also understand that the fees
                    paid for this course is non refundable and shall not be
                    refunded in any case nor will the batch date be changed.
                  </span>
                  {renderFieldError(errors, "agreedTC")}
                </div>

                {isDRA && (
                  <div className="flex flex-col mt-4">
                    <span>
                      <input
                        type="checkbox"
                        name="agreedExamTC"
                        onChange={(e) =>
                          setFieldValue("agreedExamTC", e.target.checked)
                        }
                        onBlur={(e) =>
                          setFieldValue("agreedExamTC", e.target.checked)
                        }
                        required
                        className="mr-2"
                      />
                      I confirm that exam center details once submitted cannot
                      be changed under any circumstances.
                    </span>
                    {renderFieldError(errors, "agreedExamTC")}
                  </div>
                )}

                <input
                  type="submit"
                  value={isSubmitting ? "Please wait..." : "Continue"}
                  disabled={isSubmitting}
                  className="btn-primary mt-4 w-full"
                // onClick={() => {
                //   console.log("errors", errors);
                //   setShowBatchSelection(true);
                // }}
                />
              </form>
            );
          }}
        </Formik>
        {activeSampleImage && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center p-4">
            <div className="bg-white p-6 rounded-lg max-w-[95vw] md:max-w-4xl w-full h-[90vh] max-h-[90vh] shadow-lg flex flex-col items-center">
              <div className="flex justify-between items-center w-full mb-4 flex-shrink-0">
                <h3 className="text-lg font-semibold text-gray-900 capitalize">
                  {activeSampleImage === "id_card"
                    ? "ID Card"
                    : activeSampleImage}{" "}
                  Sample
                </h3>
                <button
                  type="button"
                  onClick={closeSampleModal}
                  className="text-gray-400 hover:text-gray-600 text-xl font-bold focus:outline-none"
                >
                  &times;
                </button>
              </div>

              <div
                className={`border border-gray-200 rounded bg-gray-50 w-full flex-grow ${zoomLevel > 1
                  ? "overflow-auto flex justify-start items-start"
                  : "overflow-hidden flex justify-center items-center"
                  }`}
              >
                <img
                  src={
                    activeSampleImage === "photo"
                      ? "/sample_imgs/photo.png"
                      : activeSampleImage === "signature"
                        ? "/sample_imgs/signature.png"
                        : activeSampleImage === "id_card"
                          ? "/sample_imgs/id_card.png"
                          : "/sample_imgs/degree.png"
                  }
                  alt={`${activeSampleImage} Sample`}
                  style={
                    zoomLevel > 1
                      ? {
                        width: `${zoomLevel * 100}%`,
                        maxWidth: "none",
                        height: "auto",
                      }
                      : undefined
                  }
                  className={
                    zoomLevel > 1 ? "" : "max-h-full max-w-full object-contain"
                  }
                />
              </div>

              <div className="flex gap-2 w-full mt-4 flex-shrink-0 items-center">
                <button
                  type="button"
                  onClick={() => setZoomLevel((z) => Math.max(1, z - 0.5))}
                  disabled={zoomLevel <= 1}
                  className="border border-solidBlue text-solidBlue hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed font-semibold py-2 px-4 rounded-lg transition duration-300 focus:outline-none"
                >
                  − Zoom Out
                </button>

                <span className="text-sm font-semibold text-gray-600 min-w-[3.5rem] text-center">
                  {Math.round(zoomLevel * 100)}%
                </span>

                <button
                  type="button"
                  onClick={() => setZoomLevel((z) => Math.min(3, z + 0.5))}
                  disabled={zoomLevel >= 3}
                  className="border border-solidBlue text-solidBlue hover:bg-blue-50 disabled:opacity-40 disabled:cursor-not-allowed font-semibold py-2 px-4 rounded-lg transition duration-300 focus:outline-none"
                >
                  + Zoom In
                </button>

                <button
                  type="button"
                  onClick={closeSampleModal}
                  className="flex-1 bg-solidBlue hover:bg-opacity-95 text-white font-semibold py-2 px-4 rounded-lg transition duration-300 focus:outline-none"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </FormLayout>
    );
  };

  const batchSelectionForm = () => {
    if (isDraUnderMaintenance) {
      return (
        <BaseLayout>
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <div className="w-16 h-16 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mb-4">
              <svg
                className="w-8 h-8 text-amber-600"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-2">
              Under Maintenance
            </h2>
            <p className="text-gray-600 max-w-lg mx-auto text-base">
              Enrollment for DRA batches (DRA-GRADUATE &amp; DRA-UNDERGRADUATE) is
              temporarily paused for maintenance. We will go live soon. Thank you
              for your patience.
            </p>
            <p className="text-gray-500 max-w-lg mx-auto text-sm mt-3">
              प्रिय उम्मीदवार, कृपया ध्यान दें कि DRA बैच (DRA-GRADUATE एवं
              DRA-UNDERGRADUATE) के लिए नामांकन अभी रखरखाव (Maintenance) के कारण
              अस्थायी रूप से रुका हुआ है। हम जल्द ही दोबारा शुरुआत करेंगे। धैर्य
              के लिए धन्यवाद।
            </p>
          </div>
        </BaseLayout>
      );
    }

    const columns = [
      {
        name: "",
        cell: (row: IBatch) => (
          <input
            type="radio"
            checked={selectedBatch === row}
            onChange={() => {
              setSelectedBatch(row);

              setPopupAcknowledged(false);
              setShowPopup(true);
            }}
          />
        ),
        width: "40px",
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
      },
      {
        name: "Batch Name",
        selector: (row: IBatch) => `${row.batch_name}`,
        sortable: true,
        wrap: true,
      },
      {
        name: "Language",
        selector: (row: IBatch) => `${row.language.display_name}`,
        sortable: true,
      },
      {
        name: (
          <div style={{ textWrap: "wrap", textAlign: "center" }}>
            {"Registration End Date"}
          </div>
        ),
        wrap: true,
        selector: (row: IBatch) =>
          moment(row.registration_end_date).format("ddd, DD-MMM-YYYY"),
      },
      {
        name: "Duration",
        selector: (row: IBatch) =>
          `${moment(row.from_date).format("DD-MMM-YYYY")} - ${moment(
            row.to_date,
          ).format("DD-MMM-YYYY")}`,
        sortable: true,
        wrap: true,
      },
      {
        name: "Timings",
        selector: (row: IBatch) =>
          `${moment(row.slot_start_time, ["HH:mm", "HH:mm:ss"]).format("hh:mm A")} - ${moment(row.slot_end_time, ["HH:mm", "HH:mm:ss"]).format("hh:mm A")}`,
        wrap: true,
        minWidth: "180px",
      },
      {
        name: "Seats Available",
        selector: (row: IBatch) =>
          `${row.max_learners -
          row.userEnrollmentBatchArray_aggregate.aggregate.count
          }`,
      },
    ];
    return (
      <BaseLayout>
        <div className="flex gap-10">
          <div className="col-span-2 xl:col-span-3 font-semibold mb-4 text-lg">
            Select Batch
          </div>
          <div className="col-span-2 xl:col-span-3 font-semibold mb-4 text-lg">
            Total Live Batches:
            <span className="ml-1">{batchData?.length}</span>
          </div>
        </div>

        {/* <p className="col-span-2 xl:col-span-3 font-semibold mb-4 text-lg">WE ARE CURRENTLY WORKING ON AN UPDATE AND WILL GO LIVE SOON</p> */}

        {/* <div className="italic mb-4">
          <span className="text-lg font-semibold text-orange-500">NOTICE</span>
          <p>
            <ul className="list-disc ml-4">
              <li>
                Fee submitted towards the selected batch and course is
                non-refundable. You are requested to cross-check the course and
                batch you are applying to before proceeding with the payment.
              </li>
              <li>
                Commencement of batches may be delayed beyond the set date in
                exceptions like -
                <ul>
                  <li>
                    (i) prescribed slots are not filled until last date of
                    registration
                  </li>
                  <li>
                    (ii) IIBF authorization for the batch is pending. You shall
                    receive due information in such cases.
                  </li>
                </ul>
              </li>
            </ul>
          </p>
        </div> */}
        <DataTable columns={columns} data={batchData} />
        <input
          type="batch-submit"
          value={
            selectedBatch
              ? popupAcknowledged
                ? "Continue"
                : "Please read the notice to continue"
              : "Please Select a Batch to Continue"
          }
          disabled={!selectedBatch || !popupAcknowledged}
          className="btn-primary mt-4 w-full text-center"
          onClick={() => {
            setFieldValue("batchId", selectedBatch?.id);
            setFieldValue("courseId", selectedBatch?.course.id);
            setShowBatchSelection(false);
          }}
        />
        {showPopup && (
          <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex justify-center items-center">
            <div className="bg-white p-6 rounded-lg max-w-xl shadow-lg">
              <h2 className="text-lg font-bold text-red-600 mb-4">
                ⚠️ Please Read Before Proceeding
              </h2>
              <div className="text-sm mb-4">
                <ul className="list-disc ml-5 space-y-1">
                  <li className="text-red-600">
                    Fees once paid are non-refundable. Please double-check your
                    course and batch details (language, duration, timings)
                    before making payment as they cannot be changed later.
                  </li>
                  <li>
                    100% attendance is mandatory as per IIBF guidelines.
                    Shortage in attendance will lead to disqualification from
                    training and exam.
                  </li>
                  <li>
                    Batch start dates may be delayed if:
                    <ul className="list-disc ml-5">
                      <li>Minimum registrations are not met</li>
                      <li>IIBF approval is pending</li>
                    </ul>
                    You will be informed in such cases.
                  </li>
                  {isDRA && (
                    <>
                      <li>
                        Please note that exam center details once submitted cannot
                        be changed under any circumstances.
                      </li>
                      <li>
                        If you are selecting the Undergraduate batch, please submit
                        only 10th or 12th grade documents. If you are selecting the
                        Graduate batch, please submit documents related to
                        Graduation or Post-Graduation only.
                      </li>
                    </>
                  )}
                </ul>
                {isDRA && (
                  <p className="mt-2">
                    📄 For full details,&nbsp;
                    <a
                      href="https://iibf-uploads-prod.s3.ap-south-1.amazonaws.com/dra-prospectus-english.pdf"
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sky-600  underline hover:text-blue-800"
                    >
                      download the prospectus here
                    </a>
                    .
                  </p>
                )}

                <hr className="my-3" />

                <ul className="list-disc ml-5 text-gray-700">
                  <li className="text-red-600">
                    फीस एक बार जमा करने के बाद वापस नहीं की जाएगी।
                    कृपया भुगतान करने से पहले अपने कोर्स और बैच की जानकारी (भाषा, अवधि और समय) ध्यानपूर्वक जाँच लें,
                    क्योंकि भुगतान के बाद इनमें कोई बदलाव नहीं किया जा सकता है।
                  </li>
                  <li>
                    IIBF के अनुसार 100% उपस्थिति अनिवार्य है। उपस्थिति पूरी न
                    होने पर प्रशिक्षण और परीक्षा से अयोग्य घोषित कर दिया जाएगा।
                  </li>
                  <li>
                    बैच की शुरुआत में देरी हो सकती है यदि:
                    <ul className="list-disc ml-5">
                      <li>न्यूनतम पंजीकरण नहीं हुआ हो</li>
                      <li>IIBF से स्वीकृति लंबित हो</li>
                    </ul>
                    ऐसी स्थिति में आपको जानकारी दी जाएगी।
                  </li>
                  {isDRA && (
                    <li>
                      यदि आप अंडरग्रेजुएट बैच चुन रहे हैं, तो केवल 10वीं या 12वीं
                      के दस्तावेज़ जमा करें। यदि आप ग्रेजुएट बैच चुन रहे हैं, तो
                      केवल स्नातक या स्नातकोत्तर संबंधित दस्तावेज़ जमा करें।
                    </li>
                  )}
                </ul>
                {isDRA && (
                  <p className="mt-2">
                    📄 सभी विवरणों के लिए,&nbsp;
                    <a
                      href="https://iibf-uploads-prod.s3.ap-south-1.amazonaws.com/prospectus-dra-training-hindi.pdf"
                      download
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sky-600 underline hover:text-blue-800"
                    >
                      प्रॉस्पेक्टस डाउनलोड करें
                    </a>
                    .
                  </p>
                )}
              </div>
              <div className="text-right">
                <button
                  onClick={() => {
                    setShowPopup(false);
                    setPopupAcknowledged(true);
                  }}
                  className="btn-primary"
                >
                  I have read and agreed on disclaimer (मैं सहमत हूं)
                </button>
              </div>
            </div>
          </div>
        )}
      </BaseLayout>
    );
  };
  const errorPage = () => {
    return (
      <div className="text-red-500">
        Dear Candidate, please note that enrollment for April batches - Debt
        Recovery Agent (DRA), is temporarily paused at CSC Academy. We will
        resume soon and this page will be updated. Thank you for your patience.
        <br />
        प्रिय उम्मीदवार, कृपया ध्यान दें कि CSC Academy में DRA अप्रैल बैच के
        लिए नामांकन अभी रुका हुआ है। हम जल्द ही दोबारा शुरुआत करेंगे और यह पेज
        अपडेट कर दिया जाएगा। धैर्य के लिए धन्यवाद।
      </div>
    );
  };

  return showBatchSelection ? batchSelectionForm() : personalDetailForm();
};

const uploadFileAndSetIdAsValue = async (
  file: File,
  valueSetter: RegistrationFormContextShape["setFieldValue"],
  key: keyof RegistrationFormContextShape["formState"],
) => {
  const fileUploadResponse = await uploadFile(file);
  valueSetter(key, Number(fileUploadResponse.id));
};

// File checks

type ZodRefinerFactory<T> = [(arg: T) => Promise<boolean> | boolean, string];

const fileTypeValidator = (): ZodRefinerFactory<File> => [
  (e: File) => ["image/png", "image/jpeg"].includes(e.type),
  "File should be .jpg, .jpeg or .png, .jpg, .jpeg",
];
const maxFileSizeValidator = (maxFileSize: number): ZodRefinerFactory<File> => [
  (e: File) => e.size <= maxFileSize,
  "File should be less than or equal to " +
  Math.floor(maxFileSize / 1024) +
  "KB",
];
const minFileSizeValidator = (minFileSize: number): ZodRefinerFactory<File> => [
  (e: File) => e.size >= minFileSize,
  "File should be more than or equal to " +
  Math.floor(minFileSize / 1024) +
  "KB",
];

const PersonalDetailsFormResponseSchema = z
  .object({})
  .merge(
    CreateLearnerPayload.omit({
      highestQualificationCertificateFileId: true,
      identityProofFileId: true,
      candidatePhotoFileId: true,
      candidateSignatureFileId: true,
    }),
  )
  .merge(
    z.object({
      pincode: CreateLearnerPayload.shape.pincode.refine(
        (p) => resolvers.IND(p).then((e) => !!e),
        "Pincode is invalid",
      ),

      identityProofFile: z
        .instanceof(File, { message: "Required" })
        .refine(...fileTypeValidator())
        .refine(...minFileSizeValidator(102400))
        .refine(...maxFileSizeValidator(5242880)),
      highestQualificationCertificateFile: z
        .instanceof(File, { message: "Required" })
        .refine(...fileTypeValidator())
        .refine(...minFileSizeValidator(102400))
        .refine(...maxFileSizeValidator(5242880)),
      candidatePhotoFile: z
        .instanceof(File, { message: "Required" })
        .refine(...fileTypeValidator())
        .refine(...minFileSizeValidator(102400))
        .refine(...maxFileSizeValidator(5242880)),
      candidateSignatureFile: z
        .instanceof(File, { message: "Required" })
        .refine(...fileTypeValidator())
        .refine(...minFileSizeValidator(102400))
        .refine(...maxFileSizeValidator(5242880)),

      acceptedTC: z
        .boolean({ message: "You must accept the acknowledgement" })
        .refine((e) => e, "You must accept the acknowledgement"),

      batchId: z.number().nullable(),
      courseId: z.number().nullable(),
      agreedTC: z
        .boolean({ message: "You must accept the agreement" })
        .refine((e) => e, "You must accept the agreement"),
      agreedExamTC: z
        .boolean({ message: "You must accept the agreement" })
        .refine((e) => e, "You must accept the agreement")
        .optional(),
    }),
  );

type PersonalDetailsFormrResponseShape = z.infer<
  typeof PersonalDetailsFormResponseSchema
>;
